import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "../ui/alert";
import { API_ENDPOINTS } from "@/constants/constants";

// Définition de l'interface pour l'état de vérification
interface VerificationState {
  status: "pending" | "loading" | "success" | "error";
  message: string;
  error: string | null;
}

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [verificationState, setVerificationState] = useState<VerificationState>(
    {
      status: "pending",
      message: "",
      error: null,
    }
  );

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setVerificationState({
          status: "error",
          message: "No verification token found",
          error: "Missing token",
        });
        return;
      }

      try {
        setVerificationState({
          status: "loading",
          message: "Verifying your email address...",
          error: null,
        });

        const response = await fetch(
          `${API_ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${token}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        // Log la réponse pour le débogage
        console.log("Verify Email Response:", data);

        if (response.ok && data.success) {
          // Loguer toutes les données pour le débogage
          console.log("Email verification successful:", data);

          setVerificationState({
            status: "success",
            message: `${data.message || "Email verified successfully!"} ${
              data.isVerified ? "(Verified flag set to true)" : ""
            }`,
            error: null,
          });

          setTimeout(() => {
            navigate("/login", {
              state: {
                message: "Email verified successfully! You can now log in.",
              },
            });
          }, 2000);
        } else {
          // Handle specific error cases from the API
          let errorMessage = data.message || "Verification failed";
          console.error("Verification error details:", data);

          if (
            errorMessage.includes("token") &&
            (errorMessage.includes("expired") ||
              errorMessage.includes("invalid"))
          ) {
            errorMessage = "Token invalide ou expiré";
          }

          throw new Error(errorMessage);
        }
      } catch (error) {
        setVerificationState({
          status: "error",
          message: "Failed to verify email",
          error:
            error instanceof Error ? error.message : "Unknown error occurred",
        });
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const renderContent = () => {
    switch (verificationState.status) {
      case "loading":
      case "pending":
        return (
          <div className="flex flex-col items-center justify-center space-y-4">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <p className="text-lg font-medium text-gray-700">
              Please wait while we verify your email address...
            </p>
          </div>
        );

      case "success":
        return (
          <Alert className="bg-green-50 border-green-200">
            <AlertTitle className="text-green-800">
              Verification Successful!
            </AlertTitle>
            <AlertDescription className="text-green-700">
              {verificationState.message}
              <br />
              Redirecting you to login page...
            </AlertDescription>
          </Alert>
        );

      case "error":
        return (
          <div className="space-y-4">
            <Alert variant="destructive">
              <AlertTitle>Verification Failed</AlertTitle>
              <AlertDescription>
                {verificationState.message}
                {verificationState.error && (
                  <p className="mt-2 text-sm">
                    Error details: {verificationState.error}
                  </p>
                )}
              </AlertDescription>
            </Alert>

            {(verificationState.error === "Token invalide ou expiré" ||
              verificationState.error?.includes("expired") ||
              verificationState.error?.includes("invalid")) && (
              <div className="flex flex-col items-center justify-center mt-4">
                <p className="text-gray-700 mb-3">
                  The verification link has expired or is invalid. Please
                  request a new verification link.
                </p>
                <Link
                  to="/login"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Go to Login
                </Link>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
          Email Verification
        </h1>
        {renderContent()}
      </div>
    </div>
  );
};

export default VerifyEmail;
